import { useMemo } from 'react'
import {
	Routes,
	Route,
	Outlet,
	Navigate,
	useParams,
} from 'react-router-dom'
import { ChakraProvider } from '@chakra-ui/react'
import { IntlProvider } from 'react-intl'
import { useRecoilValue } from 'recoil'
import PullToRefresh from 'react-simple-pull-to-refresh'

import { landscapeOrientationState, confModeState } from '../../state'
import { useAppSelector } from '../../hooks'
import { TCHead } from './TCHead'
import { MasterWrap, SimplePageWrap } from '../layout'
import TCAppV3 from '../../TCAppV3'
import NativeApp from '../../NativeApp'
import { NoMatchRoute } from './NoMatchRoute'
import { theme } from '../../theme'
import getMessages, { type UserLocale } from '../../lang/messages'
import Privacy from '../../Privacy'
import Terms from '../../Terms'

const LocaleLayout = () => {
	const { userLocalePath } = useParams<'userLocalePath'>()
	const { userLocale } = useAppSelector(({ settings }) => settings) || 'en'
	// NEW LOCALE UPDATE HERE
	const localesPattern = /^ar|ca|de|en|es|fr|gr|it|nl|pl|pt|ru|sv|tr|zh$/
	const isValidLocale = localesPattern.test(`${userLocalePath}`)

	return isValidLocale
		? <Outlet />
		: <Navigate to={`/${userLocale}`} replace />
}

export const RoutedApp = () => {
	const landscapeOrientation = useRecoilValue(landscapeOrientationState)
	const { userLocale } = useAppSelector(({ settings }) => settings)
	const safeUserLocale = userLocale ? userLocale : 'en'
	const localeMessages = useMemo(
		() => userLocale
			? getMessages(userLocale)
			: getMessages('en' as UserLocale),
		[userLocale],
	)
	const confMode = useRecoilValue(confModeState)
	const showGallery = !confMode && process.env.REACT_APP_SELF_HOSTED !== 'true'

	return (
		<IntlProvider
			messages={localeMessages}
			locale="en"
			defaultLocale="en"
		>
			<TCHead locale={userLocale} />
			<ChakraProvider theme={theme}>
				<PullToRefresh
					isPullable={!landscapeOrientation}
					onRefresh={async () => window.location.reload()}
					pullingContent={''}
					refreshingContent={''}
				>
					<Routes>
						{/* Simple Page Routes - No MasterWrap */}
						<Route path="/app" element={<SimplePageWrap><Navigate to={`/${safeUserLocale}/app`} replace /></SimplePageWrap>} />
						<Route path="/privacy" element={<SimplePageWrap><Navigate to={`/${safeUserLocale}/privacy`} replace /></SimplePageWrap>} />
						<Route path="/terms" element={<SimplePageWrap><Navigate to={`/${safeUserLocale}/terms`} replace /></SimplePageWrap>} />
						<Route path=":userLocalePath/app" element={<SimplePageWrap><NativeApp /></SimplePageWrap>} />
						<Route path=":userLocalePath/privacy" element={<SimplePageWrap><Privacy /></SimplePageWrap>} />
						<Route path=":userLocalePath/terms" element={<SimplePageWrap><Terms /></SimplePageWrap>} />
						
						{/* Dashboard Routes - With MasterWrap */}
						<Route path="/" element={<LocaleLayout />}>
							{/* Redirect Routes */}
							<Route path="/" element={<Navigate to={`/${safeUserLocale}`} replace />} />
							<Route path="/undefined" element={<Navigate to={`/${safeUserLocale}`} replace />} />
							<Route path="/conference" element={<Navigate to={`/${safeUserLocale}/conference`} replace />} />
							{/* <Route path="/calculator" element={<Navigate to={`/${safeUserLocale}/calculator`} replace />} /> */}
							{showGallery && (
								<Route path="/gallery" element={<Navigate to={`/${safeUserLocale}/gallery`} replace />} />
							)}
							<Route path="/search" element={<Navigate to={`/${safeUserLocale}/search`} replace />} />
							<Route path="/date" element={<Navigate to={`/${safeUserLocale}/date`} replace />} />
							
							{/* Primary App Routes */}
							<Route path=":userLocalePath/">
								<Route index element={<MasterWrap><TCAppV3 /></MasterWrap>} />
								<Route path="block/:blockHeight" element={<MasterWrap><TCAppV3 /></MasterWrap>} />
								<Route path="conference" element={<MasterWrap><TCAppV3 /></MasterWrap>} />
								{/* <Route path="calculator" element={<MasterWrap><TCAppV3 /></MasterWrap>} /> */}
								{showGallery && (
									<Route path="gallery" element={<MasterWrap><TCAppV3 /></MasterWrap>} />
								)}
								<Route path="search" element={<MasterWrap><TCAppV3 /></MasterWrap>} />
								<Route path="date" element={<MasterWrap><TCAppV3 /></MasterWrap>} />
							</Route>
							<Route path="*" element={<NoMatchRoute />} />
						</Route>
					</Routes>
				</PullToRefresh>
			</ChakraProvider>
		</IntlProvider>
	)
}
